import { useEffect, useMemo, useRef, useState, type FormEvent } from 'react';
import { StatusCard } from '@/components/ui/card';
import { Callout } from '@/components/ui/callout';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { TraceabilityApiClient } from '@/features/traceability/api/TraceabilityApiClient';
import type { ToolResponse } from '@/features/traceability/api/TraceabilityResponseType';
import { useTranslation } from '@/hooks/useTranslation';
import { useToolSummaries } from '@/features/traceability/hooks/useToolSummaries';
import { pickersApplyTo, toolWriteErrorMessage } from '@/features/traceability/utils/toolForm';
import { ExistingToolIds } from '@/features/traceability/components/pickers/ExistingToolIds';
import { takenToolId } from '@/features/traceability/components/pickers/pickerHelpers';
import { ReferencePicker } from '@/features/traceability/components/pickers/ReferencePicker';
import { useApiClient } from '@/hooks/useApiClient';
import type { Result } from '@/lib/http/HttpClient';
import { cn } from '@/lib/utils';

type RegisterToolFormProps = {
  className?: string;
};

export const RegisterToolForm: React.FC<RegisterToolFormProps> = ({ className = '' }) => {
  const http = useApiClient();
  const { t } = useTranslation();
  const { summaries: companySummaries, refresh: refreshSummaries } = useToolSummaries();
  const [toolId, setToolId] = useState<string>('');
  const [reference, setreference] = useState<string>('');
  const [entreprise, setEntreprise] = useState('');
  const [result, setResult] = useState<Result<ToolResponse> | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const submitControllerRef = useRef<AbortController | null>(null);
  // The lists are the admin's own company: another typed company gets plain fields.
  const summaries = pickersApplyTo(entreprise) ? companySummaries : null;
  const taken = takenToolId(toolId, summaries);

  const traceabilityApiClient = useMemo(
    () => (http ? new TraceabilityApiClient(http) : null),
    [http]
  );

  useEffect(() => {
    return () => {
      submitControllerRef.current?.abort();
    };
  }, []);

  async function handleSubmit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();

    if (!traceabilityApiClient || taken) {
      return;
    }

    submitControllerRef.current?.abort();
    const ac = new AbortController();
    submitControllerRef.current = ac;

    setResult(null);
    setIsLoading(true);

    try {
      const res = await traceabilityApiClient.toolwrite(
        {
          tool: {
            toolId: toolId.trim(),
            reference: reference.trim(),
            companyName: entreprise,
            traces: [],
          },
        },
        { signal: ac.signal }
      );

      if (ac.signal.aborted) return;

      setResult(
        res.ok
          ? res
          : { ...res, message: toolWriteErrorMessage(res, t, 'admin.registerToolForm.errors.default') }
      );
      if (res.ok) {
        refreshSummaries();
        setToolId('');
        setreference('');
        setEntreprise('');
      }
    } catch {
      if (ac.signal.aborted) return;
      setResult({
        ok: false,
        code: 0,
        name: 'NetworkError',
        timestamp: new Date().toISOString(),
        message: t('admin.registerToolForm.errors.default'),
      });
    } finally {
      if (!ac.signal.aborted) setIsLoading(false);
    }
  }

  return (
    <form onSubmit={handleSubmit} className={cn('w-full', className)}>
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-[1.4fr_1fr]">

        {/* Left: tool fields */}
        <div className="flex flex-col gap-4">
          <div className="grid items-start gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="tool-id">{t('admin.registerToolForm.fields.toolId.label')}</Label>
              <Input
                id="tool-id"
                type="text"
                placeholder={t('admin.registerToolForm.fields.toolId.placeholder')}
                value={toolId}
                onChange={(e) => setToolId(e.target.value)}
                aria-invalid={taken ? true : undefined}
                aria-describedby="tool-id-existing"
                className={cn('font-mono', taken && 'border-destructive')}
                required
              />
              <ExistingToolIds
                id="tool-id-existing"
                value={toolId}
                summaries={summaries}
                onUse={setToolId}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="tool-reference">
                {t('admin.registerToolForm.fields.reference.label')}
              </Label>
              <ReferencePicker
                id="tool-reference"
                value={reference}
                onChange={setreference}
                summaries={summaries}
                placeholder={t('admin.registerToolForm.fields.reference.placeholder')}
                required
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="tool-company">{t('admin.registerToolForm.fields.company.label')}</Label>
            <Input
              id="tool-company"
              type="text"
              placeholder={t('admin.registerToolForm.fields.company.placeholder')}
              value={entreprise}
              onChange={(e) => setEntreprise(e.target.value)}
              required
            />
          </div>
        </div>

        {/* Right: info + submit */}
        <div className="flex flex-col gap-4">
          <Callout tone="neutral">{t('admin.registerToolForm.info')}</Callout>

          <div className="flex flex-col gap-3">
            <Button type="submit" className="w-full" disabled={taken !== null} loading={isLoading}>
              {t('admin.registerToolForm.submit')}
            </Button>
            {result && !result?.ok && (
              <StatusCard
                type="error"
                message={result?.message ?? t('admin.registerToolForm.errors.default')}
                className="self-stretch"
              />
            )}
            {result?.ok && (
              <StatusCard
                type="success"
                message={t('admin.registerToolForm.success')}
                className="self-stretch"
              />
            )}
          </div>
        </div>

      </div>
    </form>
  );
};
